import { Subject } from 'rxjs';
import extend from 'just-extend';
import justClone from 'just-clone';

import { environment } from '@env/environment';
import { CONSTANTS } from '@constants';
import { IBitfStorage } from './bitf-storage.interface';

export abstract class BitfStorageService<T> {
  storage$ = new Subject<T>();
  protected storageKey = `${environment.appName}-storage`;
  protected nativeStorage: Storage;
  protected StorageClass: new (data?: Partial<T>) => T;
  private initialData: Partial<T>;

  // NOTE: inital data must be an object to be passed in the StoreClass constructor not a storeClass instance
  // this is to avoid to create something like new StoreClass(justClone(StoreClassInstance)); which will
  // lead to problems
  constructor({
    initialData,
    StorageClass,
    nativeStorage = localStorage,
    storageKey,
  }: {
    initialData?: Partial<T>;
    StorageClass?: new (data?: Partial<T>) => T;
    nativeStorage?: Storage;
    storageKey?: string;
  } = {}) {
    if (!StorageClass) {
      throw new Error('StorageClass is undefined');
    }
    this.StorageClass = StorageClass;
    this.nativeStorage = nativeStorage;
    this.initialData = initialData || ({} as T);
    if (storageKey) {
      this.storageKey = `${environment.appName}-${storageKey}`;
    }
    this.initStorage();
    this.validateStorage();
  }

  private initStorage() {
    if (!this.storage) {
      this.storage = this.getEmptyStorage();
    }
  }

  private getEmptyStorage() {
    return {
      createdAt: Date.now(),
      modifiedAt: Date.now(),
      version: CONSTANTS.LOCAL_STORAGE_VERSION,
      data: new this.StorageClass(justClone(this.initialData)),
    };
  }

  // NOTE: consider this object as immutable, since every thime this getter runs you'll receive a new
  // object. This could be also a performance bottleneck in case we've tons of readings from the local storage
  get storage(): IBitfStorage<T> {
    const storageData = JSON.parse(this.nativeStorage.getItem(this.storageKey));
    if (storageData && storageData.data) {
      return { ...storageData, data: new this.StorageClass(storageData.data) };
    }
    return storageData;
  }

  set storage(storage: IBitfStorage<T>) {
    this.nativeStorage.setItem(this.storageKey, JSON.stringify(storage));
  }

  get data(): T {
    if (this.storage && this.storage.data) {
      return new this.StorageClass(this.storage.data);
    }
    return new this.StorageClass();
  }

  set data(data: T) {
    const storage = this.storage;
    storage.data = extend(this.storage.data, data);
    this.updateStorage(storage);
  }

  setData(data: T) {
    const storage = this.storage;
    storage.data = data;
    this.updateStorage(storage);
  }

  clearData() {
    const storage = this.storage;
    storage.data = {} as T;
    this.updateStorage(storage);
  }

  resetStorage() {
    const storage = this.migrateStorage();
    this.storage = storage;
    this.storage$.next(storage.data);
  }

  migrateStorage() {
    return this.getEmptyStorage();
  }

  private validateStorage() {
    const storage = this.storage;
    if (storage.version !== CONSTANTS.LOCAL_STORAGE_VERSION) {
      this.resetStorage();
    }
  }

  private updateStorage(storage: IBitfStorage<T>) {
    storage.modifiedAt = Date.now();
    this.storage = storage;
    this.storage$.next(storage.data);
  }
}
